import { useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';

const SITE_NAME = 'Axicor DWC LLC';
const SITE_URL = 'https://www.axicor.ae';
const DEFAULT_IMAGE = `${SITE_URL}/brand/axicor-logo.png`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

/* Google truncates around 155 characters, so clamp on a word boundary rather than mid-word —
   a description that ends "cutting weight and therma" reads like a broken page. */
export const clampText = (text, max = 155) => {
  const clean = String(text || '').replace(/\s+/g, ' ').trim();
  if (clean.length <= max) return clean;
  const cut = clean.slice(0, max);
  const stop = Math.max(cut.lastIndexOf('. '), cut.lastIndexOf(' '));
  return `${cut.slice(0, stop > max * 0.6 ? stop : max).replace(/[,;:.\s]+$/, '')}…`;
};

/** Per-page social card rendered by the backend, so every page shares to its own title. */
const ogCard = ({ title, description, kicker, lang }) => {
  if (!BACKEND || !title) return DEFAULT_IMAGE;
  const q = new URLSearchParams({
    title: String(title).slice(0, 120),
    subtitle: String(description || '').slice(0, 160),
    lang: lang || 'en',
  });
  if (kicker) q.set('kicker', String(kicker).slice(0, 70));
  return `${BACKEND}/api/og.png?${q.toString()}`;
};

export default function SEO({
  title,
  description = '',
  canonical,
  type = 'website',
  image,
  kicker,
  keywords = '',
  jsonLd,
  noindex = false,
}) {
  const { lang } = useLanguage();
  // Ensure title is always a valid string
  const fullTitle = (title && typeof title === 'string' && title.trim()) 
    ? `${title} | ${SITE_NAME}` 
    : `${SITE_NAME} - Global Steel & Specialty Materials Supplier`;
  const enUrl = canonical ? `${SITE_URL}${canonical}` : SITE_URL;
  const arUrl = canonical && canonical !== '/' ? `${SITE_URL}/ar${canonical}` : `${SITE_URL}/ar`;
  const fullCanonical = lang === 'ar' ? arUrl : enUrl;
  const safeDescription = clampText(description);
  const socialImage = image || ogCard({ title, description: safeDescription, kicker, lang });

  // Use React 19's native document metadata support with useEffect fallback
  useEffect(() => {
    // Update document title
    document.title = fullTitle;
    
    // Update or create meta tags
    const updateMeta = (name, content, property = false) => {
      const attr = property ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attr}="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content || '');
    };

    // Standard meta tags
    updateMeta('description', safeDescription);
    if (keywords) updateMeta('keywords', keywords);

    // Open Graph
    updateMeta('og:title', fullTitle, true);
    updateMeta('og:description', safeDescription, true);
    updateMeta('og:type', type, true);
    updateMeta('og:url', fullCanonical, true);
    updateMeta('og:image', socialImage, true);
    updateMeta('og:image:width', '1200', true);
    updateMeta('og:image:height', '630', true);
    updateMeta('og:image:alt', fullTitle, true);
    updateMeta('og:site_name', SITE_NAME, true);
    updateMeta('og:locale', lang === 'ar' ? 'ar_AE' : 'en_AE', true);
    updateMeta('og:locale:alternate', lang === 'ar' ? 'en_AE' : 'ar_AE', true);

    // Twitter Card
    updateMeta('twitter:card', 'summary_large_image');
    updateMeta('twitter:title', fullTitle);
    updateMeta('twitter:description', safeDescription);
    updateMeta('twitter:image', socialImage);
    updateMeta('twitter:image:alt', fullTitle);

    // Canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', fullCanonical);

    // hreflang alternates so Google serves the right language per path — this replaces the
    // static pair in index.html, which only describes the homepage
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => el.remove());
    [['en', enUrl], ['ar', arUrl], ['x-default', enUrl]]
      .forEach(([code, href]) => {
        const link = document.createElement('link');
        link.setAttribute('rel', 'alternate');
        link.setAttribute('hreflang', code);
        link.setAttribute('href', href);
        link.setAttribute('data-seo-hreflang', 'true');
        document.head.appendChild(link);
      });

    // Robots — always written, so a noindex page does not leave the tag behind on the next route
    let robotsMeta = document.querySelector('meta[name="robots"]');
    if (!robotsMeta) {
      robotsMeta = document.createElement('meta');
      robotsMeta.setAttribute('name', 'robots');
      document.head.appendChild(robotsMeta);
    }
    robotsMeta.setAttribute('content', noindex
      ? 'noindex,nofollow'
      : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');

    // JSON-LD
    document.querySelectorAll('script[type="application/ld+json"][data-seo]').forEach(el => el.remove());
    if (jsonLd) {
      const blocks = Array.isArray(jsonLd) ? jsonLd.filter(Boolean) : [jsonLd];
      blocks.forEach(block => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-seo', 'true');
        script.textContent = JSON.stringify(block);
        document.head.appendChild(script);
      });
    }
  }, [fullTitle, safeDescription, keywords, fullCanonical, enUrl, arUrl, type, socialImage, noindex, jsonLd, lang]);

  return null; // No visual output - just side effects
}

// Pre-built JSON-LD schemas
export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": "https://www.axicor.ae/#organization",
  "name": "Axicor DWC LLC",
  "alternateName": "Axicor",
  "url": "https://www.axicor.ae",
  "logo": {
    "@type": "ImageObject",
    "url": DEFAULT_IMAGE,
    "width": 2000,
    "height": 503
  },
  "image": DEFAULT_IMAGE,
  "description": "Dubai (DWC free zone) stockist and supplier of specialty steel and high-performance alloys: wear resistant plate, stainless, duplex and super duplex, nickel alloys, high strength steel, alloy steel, titanium, armour plate and UHMW-PE, supplied with EN 10204 3.1 mill certificates.",
  "foundingDate": "2014",
  "telephone": "+971-4-357-9568",
  "email": "sales@axicor.ae",
  "slogan": "Tell us the job, not just the grade.",
  "knowsAbout": [
    "Wear resistant steel plate", "Hardox 400", "Hardox 450", "Hardox 500",
    "Armox armour plate", "Strenx high strength steel", "Stainless steel 304 and 316L",
    "Duplex 2205 and super duplex 2507", "Inconel 625", "Monel 400", "Hastelloy",
    "Titanium Grade 2 and Grade 5", "UHMW-PE", "EN 10204 3.1 mill certificates",
    "Steel plate profiling and cutting", "Seamless and welded pipe supply",
    "Material grade selection for oil and gas, petrochemical, cement, mining and pharmaceutical plant"
  ],
  "areaServed": [
    { "@type": "Country", "name": "United Arab Emirates" },
    { "@type": "Place", "name": "GCC" },
    { "@type": "Place", "name": "Worldwide" }
  ],
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "Unit 121, Building A5",
    "addressLocality": "Dubai World Central",
    "addressRegion": "Dubai",
    "postalCode": "390667",
    "addressCountry": "AE"
  },
  "contactPoint": [{
    "@type": "ContactPoint",
    "telephone": "+971-4-357-9568",
    "contactType": "sales",
    "email": "sales@axicor.ae",
    "areaServed": "Worldwide",
    "availableLanguage": ["English", "Arabic"]
  }],
  "sameAs": []
};

/* Tells search and answer engines this is a real trading desk with a real address */
export const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": "https://www.axicor.ae/#localbusiness",
  "name": "Axicor DWC LLC",
  "image": DEFAULT_IMAGE,
  "url": "https://www.axicor.ae",
  "telephone": "+971-4-357-9568",
  "email": "sales@axicor.ae",
  "priceRange": "$$",
  "currenciesAccepted": "USD, AED",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "Unit 121, Building A5",
    "addressLocality": "Dubai World Central",
    "addressRegion": "Dubai",
    "postalCode": "390667",
    "addressCountry": "AE"
  },
  "geo": { "@type": "GeoCoordinates", "latitude": 24.8963, "longitude": 55.1612 },
  "openingHoursSpecification": [{
    "@type": "OpeningHoursSpecification",
    "dayOfWeek": ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday"],
    "opens": "09:00",
    "closes": "18:00"
  }],
  "parentOrganization": { "@id": "https://www.axicor.ae/#organization" }
};

/* Enables the sitelinks search box and names the site for AI answer engines */
export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": "https://www.axicor.ae/#website",
  "url": "https://www.axicor.ae",
  "name": "Axicor DWC LLC",
  "inLanguage": ["en", "ar"],
  "publisher": { "@id": "https://www.axicor.ae/#organization" },
  "potentialAction": {
    "@type": "SearchAction",
    "target": { "@type": "EntryPoint", "urlTemplate": "https://www.axicor.ae/grades?q={search_term_string}" },
    "query-input": "required name=search_term_string"
  }
};

export function videoSchema({ name, description, thumbnailUrl, contentUrl, uploadDate }) {
  return {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    "name": name,
    "description": description,
    "thumbnailUrl": thumbnailUrl ? [`${SITE_URL}${thumbnailUrl}`.replace(`${SITE_URL}http`, 'http')] : [DEFAULT_IMAGE],
    "contentUrl": contentUrl ? `${SITE_URL}${contentUrl}`.replace(`${SITE_URL}http`, 'http') : undefined,
    "uploadDate": uploadDate || '2026-01-01T00:00:00+04:00',
    "publisher": { "@id": "https://www.axicor.ae/#organization" }
  };
}

export function itemListSchema(items, listName) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": listName,
    "numberOfItems": items.length,
    "itemListElement": items.map((it, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": it.name,
      "url": `${SITE_URL}${it.url}`
    }))
  };
}

/* Availability has to match reality: claiming InStock on a mill indent grade is misleading
   structured data, and answer engines will repeat the claim to a buyer. */
export const availabilityFor = (stock) => {
  const s = String(stock || '').toLowerCase();
  if (!s) return 'https://schema.org/InStock';
  if (s.includes('indent') || s.includes('order')) return 'https://schema.org/BackOrder';
  if (s.includes('transit') || s.includes('arriv')) return 'https://schema.org/PreOrder';
  return 'https://schema.org/InStock';
};

export function productSchema(product) {
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": product.name,
    "description": product.description,
    "category": product.category,
    "brand": product.brand ? { "@type": "Brand", "name": product.brand } : undefined,
    "manufacturer": product.brand ? { "@type": "Organization", "name": product.brand } : undefined,
    "sku": product.slug || undefined,
    "offers": {
      "@type": "Offer",
      "availability": availabilityFor(product.stock_status),
      "availabilityStarts": undefined,
      "itemCondition": "https://schema.org/NewCondition",
      "businessFunction": "http://purl.org/goodrelations/v1#Sell",
      "priceSpecification": {
        "@type": "PriceSpecification",
        "priceCurrency": "USD",
        "valueAddedTaxIncluded": false,
        "description": "Quoted per tonne or per piece on enquiry — send thickness, size and quantity."
      },
      "url": product.url ? `${SITE_URL}${product.url}` : SITE_URL,
      "seller": { "@id": "https://www.axicor.ae/#organization" }
    },
    "material": product.category,
    "image": product.image_url || DEFAULT_IMAGE,
  };
}

export function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": item.name,
      "item": `https://www.axicor.ae${item.url}`,
    })),
  };
}

export function faqSchema(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer,
      },
    })),
  };
}
